import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { MESSAGE_TYPE } from "../../../../common/constants/common.constants";
import { NftListing } from "../../../../common/enums/common.enums";
import { NftSaleMarketplace } from "../../../../common/enums/marketplace.enums";
import {
  IClubBasicInfo,
  IClubTreasury,
} from "../../../../common/interfaces/clubs.interface";
import { IDetailNftData } from "../../../../common/interfaces/common.interface";
import { vaultStore } from "../../../../contexts/vaultStore";
import { getNftsOnSale } from "../../../../solana/common/helpers/common.helpers";
import { createNotification } from "../../../../utilities/notification";
import TableFilterItem from "../../../UserDashboard/DashboardTable/TableFilterItem/TableFilterItem";
import VaultGallery from "../VaultGallery/VaultGallery";
import NftActionsTab from "./NftActionsTab/NftActionsTab";
import "./Vault.scss";
import { clubsStore } from "../../../../contexts/clubStore";

const Vault: FC<{
  nfts?: IDetailNftData[];
  treasuryData?: IClubTreasury;
  clubBasicInfoData?: IClubBasicInfo;
  isSummaryStep?: boolean;
}> = ({ clubBasicInfoData, treasuryData, isSummaryStep, nfts }) => {
  const [isLoadingNfts, toggleIsLoadingNfts] = useState(true);
  const { treasury, vault, clubBasicInfo } = clubsStore();
  const {
    allNfts,
    nftsOnSale,
    setActiveNfts,
    activeNfts,
    nftsOnBids,
    saleNftMarketplace,
    setNftsOnBids,
    setAllNfts,
    setNftsOnSale,
    setSaleNftMarketplace,
  } = vaultStore();

  useEffect(() => {
    setActiveNfts(NftListing.AllNfts);
    void fetchNftsOnSale();
  }, []);

  const showNftToggles = useMemo(() => {
    return (
      nftsOnSale.length > 0 ||
      nftsOnBids.length > 0 ||
      allNfts.length > 0 ||
      (vault?.nfts && vault?.nfts.length > 0)
    );
  }, [nftsOnBids, nftsOnSale, allNfts, vault]);

  const fetchNftsOnSale = async () => {
    try {
      const { buyPeerToPeer, nftsOnSale } = await getNftsOnSale(
        (treasury ? treasury.treasury : treasuryData?.treasury)!,
        (clubBasicInfo ? clubBasicInfo.address : clubBasicInfoData?.address)!
      );
      setNftsOnSale(nftsOnSale);
      setNftsOnBids(buyPeerToPeer);
      setAllNfts(vault ? vault.nfts : nfts!);
      toggleIsLoadingNfts(false);
    } catch (error) {
      createNotification(MESSAGE_TYPE.ERROR, "Failed to load NFTs");
    }
  };

  const changeNftsOnSaleFilters = useCallback(
    (marketplace: NftSaleMarketplace) => {
      let addedMarketplaces = [...saleNftMarketplace];
      if (!saleNftMarketplace.includes(marketplace)) {
        addedMarketplaces.push(marketplace);
      } else {
        addedMarketplaces = addedMarketplaces.filter(
          (addedMarketplace) => marketplace !== addedMarketplace
        );
      }
      setSaleNftMarketplace(addedMarketplaces);
    },
    [saleNftMarketplace]
  );
  return (
    <div className="vault-section">
      <div className="vault-section__gallery-header">
        {showNftToggles && (
          <p className="vault-section__vault-details">
            <span className="vault-section__vault-label">Vault</span>
          </p>
        )}

        {showNftToggles && (
          <div className="vault-section__tabs">
            <NftActionsTab
              isLoadingNfts={isLoadingNfts}
              onClick={() => setActiveNfts(NftListing.AllNfts)}
              tabTitle="Club NFTs"
              tabAmount={vault?.nfts.length ?? allNfts.length}
              isActive={activeNfts === NftListing.AllNfts}
              tooltipTitle="List of all NFTs the club owns"
            />
            <NftActionsTab
              isLoadingNfts={isLoadingNfts}
              onClick={() => setActiveNfts(NftListing.Bids)}
              tabTitle="Buying"
              tabAmount={nftsOnBids.length}
              isActive={activeNfts === NftListing.Bids}
              tooltipTitle="List of all NFTs the club wants to buy."
            />
            <NftActionsTab
              isLoadingNfts={isLoadingNfts}
              onClick={() => setActiveNfts(NftListing.Sales)}
              tabTitle="Selling"
              tabAmount={nftsOnSale.length}
              isActive={activeNfts === NftListing.Sales}
              tooltipTitle="List of all NFTs the club wants to sell."
            />
          </div>
        )}

        <div className="vault-section__filters-wrapper">
          {activeNfts === NftListing.Sales && (
            <div className="vault-section__nft-filters">
              <TableFilterItem
                title="Peer to peer"
                onFilterItemSelect={() =>
                  changeNftsOnSaleFilters(NftSaleMarketplace.PeerToPeer)
                }
              />
              <TableFilterItem
                title="SolSea"
                onFilterItemSelect={() =>
                  changeNftsOnSaleFilters(NftSaleMarketplace.Solsea)
                }
              />
            </div>
          )}
        </div>
      </div>

      <VaultGallery
        galleryTitle=""
        isSummaryStep={isSummaryStep}
        clubBasicInfo={clubBasicInfoData ?? clubBasicInfo}
      />
    </div>
  );
};

export default Vault;
